export default function componentStyleOverrides(theme) {
	const bgColor = theme.colors?.grey50;
	return {
		MuiButton: {
			styleOverrides: {
				root: {
					fontWeight: 500,
					borderRadius: '4px',
					padding: '4px 12px',
					background: theme.colors?.secondaryLight,
					color: theme.colors?.secondaryDark,
					'&:hover': {
						background: theme.colors?.secondaryDark,
						color: theme.colors?.secondaryLight,
					},
				},
			},
		},
		MuiPaper: {
			defaultProps: {
				elevation: 0,
			},
			styleOverrides: {
				root: {
					backgroundImage: 'none',
				},
				rounded: {
					borderRadius: `${theme?.customization?.borderRadius}px`,
				},
			},
		},
		MuiCardHeader: {
			styleOverrides: {
				root: {
					color: theme.colors?.textDark,
					padding: '12px',
				},
				title: {
					fontSize: '1.125rem',
				},
			},
		},
		MuiCardContent: {
			styleOverrides: {
				root: {
					padding: '12px',
				},
			},
		},
		MuiCardActions: {
			styleOverrides: {
				root: {
					padding: '12px',
				},
			},
		},
		MuiListItemButton: {
			styleOverrides: {
				root: {
					color: theme.darkTextPrimary,
					paddingTop: '10px',
					paddingBottom: '10px',
					'&.Mui-selected': {
						color: theme.menuSelected,
						backgroundColor: theme.menuSelectedBack,
						'&:hover': {
							backgroundColor: theme.menuSelectedBack,
						},
						'& .MuiListItemIcon-root': {
							color: theme.menuSelected,
						},
					},
					'&:hover': {
						backgroundColor: theme.menuSelectedBack,
						color: theme.menuSelected,
						'& .MuiListItemIcon-root': {
							color: theme.menuSelected,
						},
					},
				},
			},
		},
		MuiListItemIcon: {
			styleOverrides: {
				root: {
					color: theme.darkTextPrimary,
					minWidth: '36px',
				},
			},
		},
		MuiListItemText: {
			styleOverrides: {
				primary: {
					color: theme.textDark,
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				input: {
					color: theme.text,
					'&::placeholder': {
						color: theme.text,
						fontSize: '0.775rem',
					},
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					background: bgColor,
					borderRadius: `${theme?.customization?.borderRadius}px`,
					'& .MuiOutlinedInput-notchedOutline': {
						borderColor: theme.colors?.grey400,
					},
					'&:hover $notchedOutline': {
						borderColor: theme.colors?.primaryLight,
					},
					'&.MuiInputBase-multiline': {
						padding: 1,
					},
				},
				input: {
					fontWeight: 400,
					background: bgColor,
					borderRadius: `${theme?.customization?.borderRadius}px`,
					'&.MuiInputBase-inputSizeSmall': {
						'&.MuiInputBase-inputAdornedStart': {
							paddingLeft: 0,
						},
					},
					'&.MuiInputBase-input': {
						'&.Mui-disabled': {
							WebkitTextFillColor: 'rgba(0, 0, 0, 0.58)',
						},
					},
				},
				inputAdornedStart: {
					paddingLeft: 4,
				},
				notchedOutline: {
					borderRadius: `${theme?.customization?.borderRadius}px`,
				},
			},
		},
		MuiSlider: {
			styleOverrides: {
				root: {
					'&.Mui-disabled': {
						color: theme.colors?.grey300,
					},
				},
				mark: {
					backgroundColor: theme.paper,
					width: '4px',
				},
				valueLabel: {
					color: theme?.colors?.primaryLight,
				},
			},
		},
		MuiDivider: {
			styleOverrides: {
				root: {
					borderColor: theme.divider,
					opacity: 1,
				},
			},
		},
		MuiAvatar: {
			styleOverrides: {
				root: {
					color: theme.colors?.primaryDark,
					background: theme.colors?.primary200,
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					'&.MuiChip-deletable .MuiChip-deleteIcon': {
						color: 'inherit',
					},
				},
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					color: theme.paper,
					background: theme.colors?.grey700,
				},
			},
		},
		MuiToolbar: {
			styleOverrides: {
				root: {
					padding: '0px',
				},
			},
		},
		MuiDataGrid: {
			styleOverrides: {
				root: {
					fontSize: '0.775rem',
				},
				main: {
					padding: '12px',
				},
				cell: {
					'&:focus, &:focus-within': {
						outline: 'none',
					},
				},
				columnHeaders: {
					backgroundColor: theme.colors?.grey300,
				},
				columnHeader: {
					'&--sorted': {
						backgroundColor: theme.colors?.grey200,
					},
					'&:focus, &:focus-within': {
						outline: 'none',
					},
				},
				menu: {
					boxShadow: '0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)',
				},
				toolbarContainer: {
					padding: '12px 12px 0px',
				},
				iconSeparator: {
					color: theme.colors?.grey500,
				},
			},
		},
		MuiTabPanel: {
			styleOverrides: {
				root: {
					padding: '10px 0',
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					fontSize: '0.815rem',
					padding: '6px 16px',
				},
			},
		},
		MuiTableContainer: {
			styleOverrides: {
				root: {
					borderTopColor: theme.divider,
				},
			},
		},
		MuiTableHead: {
			styleOverrides: {
				root: {
					textTransform: 'capitalize',
					'& .MuiTableCell-head': {
						fontSize: '0.75rem',
						fontWeight: 600,
						padding: '0.4rem 0.75rem',
						backgroundColor: theme.colors.primaryLight,
						color: theme.darkTextPrimary,
						letterSpacing: '0.13px',
					},
				},
			},
		},
		MuiTableBody: {
			styleOverrides: {
				root: {
					'& .MuiTableCell-body': {
						letterSpacing: '0.25px',
						color: theme.secondaryDark,
						'&:not(.MuiTableCell-sizeSmall):not(.MuiTableCell-paddingCheckbox):not(.MuiTableCell-paddingNone)': {
							padding: '0.4rem 0.75rem',
						},
					},
					'& .MuiTableRow-root:last-child': {
						'& .MuiTableCell-body': {
							paddingBottom: `0.8rem !important`, // Adjust the padding value as needed
						},
					},
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					'& .MuiTableCell-head:first-of-type, & .MuiTableCell-root:first-of-type': {
						paddingLeft: `0.5rem !important`,
					},
					'& .MuiTableCell-head:last-child, & .MuiTableCell-root:last-child': {
						paddingRight: `0.8rem !important`,
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					borderBottom: `1px solid ${theme.divider}`,
					'& .MuiButton-root': {
						color: theme.secondaryLight,
					},
				},
			},
		},
	};
}
